import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import Confirm from "../../../../components/confirm/Confirm";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import DeflectionSource from "../../../../components/pickers/deflection-sources/DeflectionSource";
import ExternalForms from "../../../../components/pickers/external-forms/ExternalForms";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import WorkflowDisplay, { createWorklowStep } from "../../../../components/workflow/Workflow";
import usePortal from "../../../../contexts/PortalContext";
import useRouting, { useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/UserContext";
import {
    FormField, FormFieldDescriptorInput,
    FormType,
    GetFormDataDocument, ListRequestTypesDocument, useDeleteFormMutation, useGetExternalFormFieldsQuery, useGetFormDataQuery,
    useGetFormQuery, useSaveFormMutation, useSaveWorkflowMutation, WorkflowStep,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import FormDesigner from "./FormDesigner";
import createFormFields from "./WorkflowFields";

export default function RequestTypes(): ReactElement {
    const { Paths, historyPush } = useRouting();
    const { id: portalId } = usePortal();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const nameRef = React.createRef<HTMLInputElement>();
    const userSession = useCurrentSession();

    const {
        data, error, loading: isLoading,
    } = useGetFormQuery({
        fetchPolicy: "no-cache",
        variables: {
            id: id === "new" ? "urn:fq:form:new" : id,
            hasId: id !== "new",
        },
    });

    const [externalFormId, setExternalFormId] = useState(() => data?.form?.externalFormId);

    const { data: formData, loading: isDataLoading, refetch } = useGetFormDataQuery({});
    const { data: externalData } = useGetExternalFormFieldsQuery({
        variables: {
            id: externalFormId,
        },
        skip: externalFormId == null,
    });
    const [saveForm] = useSaveFormMutation();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [deleteForm] = useDeleteFormMutation();

    const [name, setName] = useState(() => data?.form?.name ?? "");
    const [description, setDescription] = useState(() => data?.form?.description ?? "");
    const [isCreatePrivate, setIsCreatePrivate] = useState(() => data?.form?.isCreatePrivate ?? false);
    const [deflectionIdentity, setDeflectionIdentity] = useState(() => (data?.form?.deflection?.identity
        ?? (data?.form?.deflection?.isEnabled ? portalId : undefined)));
    const [workflow, setWorkflow] = useState<WorkflowStep[]>(() => data?.form?.workflow?.steps as WorkflowStep[] ?? []);

    const [userGroupIds, setUserGroupIds] = useState(() => data?.form?.userGroupIds as string[]
            ?? userSession.user?.userGroups?.toIdList("id")
            ?? []);

    const [fields, setFields] = useState<FormFieldDescriptorInput[]>(
        () => data?.form?.fields.map(field => removeTypename(field)) ?? [],
    );

    const [displayDelete, setDisplayDelete] = useState(false);

    useEffect(() => {
        if (!data) {
            return;
        }
        setName(data?.form?.name ?? "");
        setDescription(data?.form?.description ?? "");
        setFields(data?.form?.fields.map(field => removeTypename(field)) ?? []);
        setUserGroupIds(data?.form?.userGroupIds ?? []);
        setIsCreatePrivate(data?.form?.isCreatePrivate ?? false);
        setDeflectionIdentity(data?.form?.deflection?.identity
            ?? (data?.form?.deflection?.isEnabled ? portalId : undefined));
        setWorkflow(data.form?.workflow?.steps as WorkflowStep[] ?? []);
        setExternalFormId(data.form?.externalFormId);
    }, [data?.form]);

    if (isLoading || isDataLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!nameRef.current?.checkValidity()) {
            nameRef.current?.reportValidity();
            return;
        }
        (async () => {
            const workflowVersion = !workflow ? undefined : await saveWorkflow({
                variables: {
                    id: data?.form?.workflow?.id,
                    workflow: {
                        name: "Auto generated",
                        description: "Auto generated for form",
                        steps: workflow.map(createWorklowStep),
                    },
                },
            });

            await saveForm({
                refetchQueries: [
                    { query: GetFormDataDocument },
                    { query: ListRequestTypesDocument },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    form: {
                        fields: fields.map(field => ({
                            ...field,
                            __typename: undefined,
                        })),
                        type: FormType.Default,
                        name,
                        description,
                        isCreatePrivate,
                        userGroupIds,
                        workflowId: workflowVersion?.data?.saveWorkflow?.id,
                        deflection: {
                            isEnabled: deflectionIdentity != null,
                            identity: deflectionIdentity,
                        },
                        externalFormId,

                    },
                },
            });
            historyPush(Paths.ADMINISTRATOR_SETTING, {
                setting: "request-types",
            });
        })();
    };

    const onDelete = async () => {
        await deleteForm({
            refetchQueries: [
                { query: GetFormDataDocument },
            ],
            variables: {
                id,
            },
        });
        historyPush(Paths.ADMINISTRATOR_SETTING, {
            setting: "request-types",
        });
    };

    const fieldMap = formData?.formFields.toIdMap("id") ?? {};

    const formFields = (externalData?.externalFormFields as FormField[]
        ?? fields.mapFilter(row => (row.fieldId ? fieldMap[row.fieldId] : undefined)));
    return (
        <>
            <Confirm confirmText="Delete" onConfirm={onDelete} shouldShow={displayDelete} onHide={() => setDisplayDelete(false)}>
                <div>
                    Are you sure you want to delete the request type:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {name}
                    </div>

                </div>
            </Confirm>
            <div>
                <div className="mb-4 d-flex">
                    <div className="col border-end me-3 pe-3">
                        <div className="border p-3 rounded mb-4 bg-white">

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">Name</Form.Label>
                                <Form.Control
                                    ref={nameRef}
                                    type="text"
                                    placeholder="Name"
                                    required
                                    onChange={evt => setName(evt.target.value)}
                                    value={name}
                                />
                                <Form.Text>
                                    The name of the request type displayed at the top and in the request type selection.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Description
                                    {" "}
                                    <small className="text-light">(Optional)</small>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    as="textarea"
                                    placeholder="This request type is used for ..."
                                    onChange={evt => setDescription(evt.target.value)}
                                    value={description}
                                />
                                <Form.Text>
                                    The optional description is to help you understand where this request
                                    type is used. It will never be displayed to the end user.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Groups
                                </Form.Label>
                                <UserGroups
                                    isMulti
                                    canCreate
                                    value={userGroupIds}
                                    onSelected={ids => setUserGroupIds(ids)}
                                />
                                <Form.Text>
                                    Which user groups are allowed to access this request type?
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Always Private Create
                                </Form.Label>
                                <BooleanSelect
                                    isRequired
                                    value={isCreatePrivate}
                                    onChange={setIsCreatePrivate}
                                />
                                <Form.Text>
                                    When creating this request type, can the request be posted where visible
                                    by others, or should it always be private between the requester and the
                                    agent?
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Deflection Provider
                                </Form.Label>
                                <DeflectionSource
                                    value={deflectionIdentity}
                                    onSelected={setDeflectionIdentity}
                                />
                                <Form.Text>
                                    When enabled, Agent will attempt to use deflection to answer the users question.
                                    If not possible, or Agent can not find a good answer, a standard ticket will be created.
                                </Form.Text>
                            </Form.Group>
                        </div>

                        <div className="border p-3 rounded mb-4 bg-white">
                            <h5 className="mb-2 text-light">
                                Automations
                            </h5>
                            <div>
                                Automation allows you to create logic, trigger services, and take actions on this request.
                            </div>

                            <WorkflowDisplay
                                shouldShowCreatingRequest
                                value={workflow}
                                onChange={updated => setWorkflow(updated)}
                                input={createFormFields(formFields)}
                            />

                        </div>

                    </div>
                    <div className="d-flex flex-column col-4 ">

                        {!externalFormId && (
                            <div className="mb-4">
                                <FormDesigner
                                    fields={fields}
                                    formData={formData}
                                    formName={name}
                                    groupIds={userGroupIds}
                                    id={id}
                                    onCustomFieldAdded={refetch}
                                    setFields={setFields}
                                />
                            </div>
                        )}
                        <div className="border p-3 rounded bg-white">
                            <strong>External Forms</strong>
                            <ExternalForms
                                isClearable
                                value={externalFormId}
                                onSelected={value => {
                                    setExternalFormId(value);
                                }}
                            />
                            <small className="text-gray-600">
                                If you are using an integration that supports forms, attach the external form to this
                                request type instead of creating the form in Agent directly.
                            </small>
                        </div>

                    </div>
                </div>

                <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                    {id !== "new" && (
                        <button type="button" className="btn btn-none text-danger me-4" onClick={() => setDisplayDelete(true)}>
                            Delete
                        </button>
                    )}

                    <button type="submit" className="btn btn-primary" onClick={onSubmit}>
                        Save
                    </button>
                </div>

            </div>
        </>
    );
}
